import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Reveal } from '@/components/motion/reveal'
import { SplitText } from '@/components/motion/split-text'

interface Breadcrumb {
    label: string
    href?: string
}

interface PageHeroProps {
    eyebrow: string
    title: string
    lead?: string
    breadcrumbs?: Breadcrumb[]
    aside?: ReactNode
    children?: ReactNode
    titleClassName?: string
}

export const PageHero: FC<PageHeroProps> = ({
    eyebrow,
    title,
    lead,
    breadcrumbs,
    aside,
    children,
    titleClassName
}): ReactNode => {
    return (
        <section aria-labelledby='page-heading' className='relative pt-32 lg:pt-44'>
            <Container>
                {breadcrumbs && (
                    <Reveal trigger='mount' delay={0.15} y={10}>
                        <nav aria-label='Brotkrumen'>
                            <ol className='flex flex-wrap items-center gap-1.5 text-sm text-ink/55'>
                                {breadcrumbs.map((crumb, index) => (
                                    <li key={crumb.label} className='flex items-center gap-1.5'>
                                        {index > 0 && <ChevronRight aria-hidden='true' className='size-3.5 text-ink/35' strokeWidth={1.75} />}
                                        {crumb.href
                                            ? (
                                                <Link href={crumb.href} className='link-grow pb-0.5 transition-colors hover:text-ink'>
                                                    {crumb.label}
                                                </Link>
                                            )
                                            : (
                                                <span aria-current='page' className='text-ink'>
                                                    {crumb.label}
                                                </span>
                                            )}
                                    </li>
                                ))}
                            </ol>
                        </nav>
                    </Reveal>
                )}

                <Reveal trigger='mount' delay={0.25} y={12} className={breadcrumbs ? 'mt-12 lg:mt-16' : ''}>
                    <Eyebrow>
                        {eyebrow}
                    </Eyebrow>
                </Reveal>

                <div className='mt-6 grid gap-10 lg:grid-cols-12 lg:items-end lg:gap-10'>
                    <SplitText
                        as='h1'
                        id='page-heading'
                        trigger='mount'
                        delay={0.35}
                        stagger={0.07}
                        text={title}
                        className={cn(
                            'hyphenate font-heading text-[clamp(3rem,8vw,8rem)] leading-[0.92] tracking-[-0.02em] text-ink lg:col-span-8',
                            titleClassName
                        )}
                    />

                    {aside && (
                        <Reveal trigger='mount' delay={0.9} className='lg:col-span-4 lg:pb-3'>
                            {aside}
                        </Reveal>
                    )}
                </div>

                {lead && (
                    <Reveal trigger='mount' delay={0.75} y={18}>
                        <p className='mt-10 max-w-4xl font-heading text-[clamp(1.6rem,2.8vw,2.6rem)] leading-[1.15] text-ink/85 lg:mt-14'>
                            {lead}
                        </p>
                    </Reveal>
                )}

                {children}
            </Container>
        </section>
    )
}
